/* DecantGems "Added" side panel + sold-out alternatives (owner 16 Oct 2026: "where is the upsell thing ... the side bar that
   opens ... if its sold out the recommendations"). The FragranceGems / Mykonos upsell drawer, built into this theme.

   Hook: every add on a page (product page button + sticky bar in dg-sample.js, card / Brands-menu quick add in dg-quickadd.js,
   set builder "add all" in dg-set-builder.js, the "+" of the sold-out block here) ends in notifyCart() of assets/dg-fly.js,
   which dispatches Shopify's standard cart:lines-update (action add) with a promise that settles when the flying photo has
   landed. This script listens for it, fetches /cart.js and the panel section (sections/dg-upsell-panel.liquid, Section
   Rendering API, rendered for the added product) while the photo is in the air, and opens the panel when both are done.
   Not on /cart (the cart page has its own upsells). The "+" inside the panel adds, then re-renders the panel in place.
   Native <dialog> + showModal(): top layer, page inert, focus moves in, Esc / backdrop / buttons close, focus returns to the
   control that added. Without JavaScript every "+" is a plain form POST to /cart/add. */
import { CartLinesUpdateEvent } from '@shopify/events';
import { flyToCart, notifyCart } from '@dg/fly';

const SECTION = 'dg-upsell-panel';
const CLOSE_MS = 440;
let C = null;
let dialog = null;
let live = null;
let opener = null;
let lastTrigger = null;
let seed = '';
let shown = new Set();
let selfAdd = 0;
let cssReady = null;
let closing = false;
let seq = 0;

function cfg() {
  if (C) return C;
  try { C = JSON.parse(document.getElementById('dg-up-t').textContent); } catch (e) { C = {}; }
  return C;
}
function root() {
  const r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  return r || '/';
}
function onCartPage() {
  return /\/cart\/?$/.test(location.pathname);
}
function reduced() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
}
function variantId(gid) {
  return Number(String(gid || '').split('/').pop()) || 0;
}
function loadCss() {
  if (cssReady) return cssReady;
  cssReady = new Promise((resolve) => {
    const href = cfg().css;
    const have = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find((l) => /dg-upsell\.css/.test(l.href));
    if (have || !href) return resolve();
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = link.onerror = () => resolve();
    document.head.appendChild(link);
    setTimeout(resolve, 4000);
  });
  return cssReady;
}
function json(url) {
  return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) => r.json());
}
function section(handle) {
  const url = `${root()}products/${encodeURIComponent(handle)}?section_id=${SECTION}`;
  return fetch(url, { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.text() : ''))
    .then((html) => {
      if (!html) return null;
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      return tpl.content.querySelector('[data-dgup-body]');
    });
}

/* ---- dialog ---- */
function lock(on) {
  const html = document.documentElement;
  if (on) {
    const gap = window.innerWidth - html.clientWidth;
    if (gap > 0) document.body.style.paddingInlineEnd = `${gap}px`;
    html.classList.add('dgup-lock');
  } else {
    html.classList.remove('dgup-lock');
    document.body.style.paddingInlineEnd = '';
  }
}
function say(msg) {
  if (!live || !msg) return;
  live.textContent = '';
  setTimeout(() => { live.textContent = msg; }, 60);
}
function build() {
  if (dialog) return dialog;
  dialog = document.createElement('dialog');
  dialog.className = 'dgup-dialog';
  dialog.setAttribute('aria-labelledby', 'DgUpTitle');
  live = document.createElement('div');
  live.className = 'dg-vh';
  live.setAttribute('aria-live', 'polite');
  dialog.appendChild(live);
  dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) { close(); return; }
    if (e.target.closest && e.target.closest('[data-dgup-close]')) close();
  });
  dialog.addEventListener('submit', panelAdd);
  document.body.appendChild(dialog);
  return dialog;
}
function fill(body) {
  build();
  const old = dialog.querySelector('[data-dgup-body]');
  if (old) old.replaceWith(body); else dialog.appendChild(body);
  const lines = Array.from(body.querySelectorAll('[data-dgup-line]'));
  let any = false;
  lines.forEach((li) => {
    const on = shown.has(Number(li.getAttribute('data-dgup-line')));
    li.hidden = !on;
    if (on) any = true;
  });
  if (!any && lines[0]) lines[0].hidden = false;
  const list = body.querySelector('.dgup__lines');
  if (list) list.classList.toggle('dgup__lines--many', lines.filter((li) => !li.hidden).length > 2);
}
function open(trigger) {
  build();
  opener = trigger || null;
  closing = false;
  const title = dialog.querySelector('#DgUpTitle');
  if (!dialog.open) {
    try { dialog.showModal(); } catch (e) { dialog.setAttribute('open', ''); }
    lock(true);
    if (reduced()) dialog.classList.add('is-in');
    else requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-in')));
  }
  if (title) title.focus({ preventScroll: true });
}
function close() {
  if (!dialog || !dialog.open || closing) return;
  closing = true;
  dialog.classList.remove('is-in');
  setTimeout(() => {
    try { dialog.close(); } catch (e) { dialog.removeAttribute('open'); }
    lock(false);
    closing = false;
    const back = opener;
    opener = null;
    // back to the control that added; when that one is gone or hidden now (a closed menu panel), to the header cart
    const usable = (n) => n && n.isConnected && typeof n.focus === 'function' && !n.closest('[hidden], [inert]') &&
      n.getClientRects().length > 0 && getComputedStyle(n).visibility !== 'hidden';
    const cartLink = document.querySelector('cart-icon a, a.header-actions__cart-icon, header a[href$="/cart"]');
    const to = usable(back) ? back : usable(cartLink) ? cartLink : null;
    if (to) to.focus({ preventScroll: true });
  }, reduced() ? 0 : CLOSE_MS);
}

/* ---- adds that open the panel ---- */
function handleFromDom(vid) {
  const nodes = document.querySelectorAll(`input[name="id"][value="${vid}"], input[type="radio"][value="${vid}"]`);
  for (const n of nodes) {
    const row = n.closest('[data-dgup-item]');
    if (row) return row.getAttribute('data-dgup-item');
    if (n.closest('#DgProductForm, [data-dg-buy]')) {
      const m = location.pathname.match(/\/products\/([^/?#]+)/);
      if (m) return decodeURIComponent(m[1]);
    }
    const card = n.closest('.dg-card, .dg-mbest__item');
    const a = card && card.querySelector('a[href*="/products/"]');
    if (a) return decodeURIComponent(a.pathname.split('/products/')[1].split('/')[0]);
  }
  return '';
}
document.addEventListener('submit', (e) => {
  lastTrigger = e.submitter || (e.target && e.target.querySelector && e.target.querySelector('[type="submit"]')) || e.target;
}, true);
document.addEventListener('click', (e) => {
  const b = e.target && e.target.closest && e.target.closest('button, a, [role="button"]');
  if (b) lastTrigger = b;
}, true);

document.addEventListener(CartLinesUpdateEvent.eventName, (ev) => {
  if (ev.action !== 'add' || selfAdd > 0 || onCartPage() || !window.fetch) return;
  const ids = (ev.lines || []).map((l) => variantId(l.merchandiseId)).filter(Boolean);
  if (!ids.length) return;
  const trigger = lastTrigger && lastTrigger.isConnected ? lastTrigger : document.activeElement;
  const mine = ++seq;
  const ok = ev.promise
    ? Promise.resolve(ev.promise).then((r) => !(r && r.detail && r.detail.didError), () => false)
    : Promise.resolve(true);
  // the added product's handle from the page when it is there (saves a /cart.js round trip), else from the cart
  const known = handleFromDom(ids[0]);
  const body = (known ? Promise.resolve(known) : json(`${root()}cart.js`).then((cart) => {
    const items = (cart && cart.items) || [];
    const line = items.find((i) => i.variant_id === ids[0]) || items.find((i) => ids.includes(i.variant_id));
    return line ? line.handle : '';
  })).then((handle) => (handle ? section(handle).then((node) => ({ node, handle })) : null));
  Promise.all([body, ok, loadCss()])
    .then(([res, good]) => {
      if (mine !== seq || !good || !res || !res.node) return;
      seed = res.handle;
      shown = new Set(ids);
      fill(res.node);
      open(trigger);
    })
    .catch((e) => { if (window.console) console.warn('[dg-upsell]', e); });
});

function addJs(id) {
  return fetch(`${root()}cart/add.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
  }).then((r) => r.json().catch(() => ({})).then((data) => ({ ok: r.ok, status: r.status, data })));
}
function refusal(res) {
  const t = cfg();
  const d = res.data || {};
  if (res.status === 422) return (typeof d.description === 'string' && d.description) || t.soldout || t.error;
  return t.error;
}
function showMsg(row, text) {
  const msg = row && row.querySelector('.dgup-row__msg');
  if (msg) { msg.textContent = text || ''; msg.hidden = !text; }
}

/* "+" inside the panel: add, header count, re-render the panel in place */
function panelAdd(e) {
  const form = e.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-dgup-add')) return;
  e.preventDefault();
  if (form.dataset.busy) return;
  const row = form.closest('.dgup-row');
  const btn = form.querySelector('button');
  const id = Number(form.elements.id && form.elements.id.value);
  const name = (btn && btn.getAttribute('data-name')) || '';
  const slot = row ? Array.from(row.parentNode.children).indexOf(row) : 0;
  form.dataset.busy = '1';
  if (btn) btn.setAttribute('aria-busy', 'true');
  showMsg(row, '');
  addJs(id)
    .then((res) => {
      if (!res.ok) {
        if (btn) btn.removeAttribute('aria-busy');
        delete form.dataset.busy;
        showMsg(row, refusal(res));
        return;
      }
      if (btn) { btn.removeAttribute('aria-busy'); btn.classList.add('is-added'); }
      shown.add(id);
      selfAdd++;
      try { notifyCart('dg-upsell', [{ merchandiseId: String(id), quantity: 1 }], null).catch(() => {}); } finally { selfAdd--; }
      const t = cfg();
      say(t.added_status ? t.added_status.replace('%n', name) : t.added);
      return section(seed).then((node) => {
        if (!node || !dialog) return;
        fill(node);
        const rows = dialog.querySelectorAll('.dgup-row .dgup-add');
        const next = rows[Math.min(slot, rows.length - 1)] || dialog.querySelector('#DgUpTitle');
        if (next) next.focus({ preventScroll: true });
      });
    })
    .catch(() => {
      if (btn) btn.removeAttribute('aria-busy');
      delete form.dataset.busy;
      showMsg(row, cfg().error);
    });
}

/* "+" in the sold-out block of a product page: add, fly, and the panel opens through the event above */
document.addEventListener('submit', (e) => {
  const form = e.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-dgup-add')) return;
  if (dialog && dialog.contains(form)) return;
  e.preventDefault();
  if (form.dataset.busy) return;
  const row = form.closest('.dgup-row');
  const btn = form.querySelector('button');
  const id = Number(form.elements.id && form.elements.id.value);
  form.dataset.busy = '1';
  if (btn) btn.setAttribute('aria-busy', 'true');
  showMsg(row, '');
  loadCss();
  addJs(id)
    .then((res) => {
      if (btn) btn.removeAttribute('aria-busy');
      delete form.dataset.busy;
      if (!res.ok) { showMsg(row, refusal(res)); return; }
      const img = row && row.querySelector('img');
      const src = img && (img.currentSrc || img.src);
      const landed = flyToCart(src ? { src, rect: img.getBoundingClientRect() } : null);
      return notifyCart('dg-upsell-inline', [{ merchandiseId: String(id), quantity: 1 }], landed).catch(() => {});
    })
    .catch(() => {
      if (btn) btn.removeAttribute('aria-busy');
      delete form.dataset.busy;
      showMsg(row, cfg().error);
    });
});
